//* Import libraries
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//! Import API
import { teacherAPI } from "./teacherAPI";

//? Import Pagination type
import type { Pagination } from "../../services/stores/types";

//? Import types from types
import type {
  TeachersDetails,
  CreateTeacherInput,
  UpdateTeacherInput,
} from "./types/types";

//? Import types from CSVTeacher-validator
import type {
  CSVTeacher,
  CSVTeacherProcessingError,
  CSVTeacherProcessingResult,
} from "../../utils/csv/csv-validatorTeacher";

export const useTeachers = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "teachers",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      teacherAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const teachers = data?.teachers || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createTeacherMutation = useMutation({
    mutationFn: (teacherData: CreateTeacherInput) =>
      teacherAPI.create(teacherData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
    },
  });

  const updateTeacherMutation = useMutation({
    mutationFn: ({ id, teacherData }: UpdateTeacherInput) =>
      teacherAPI.update(id, teacherData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
    },
  });

  const deleteTeacherMutation = useMutation({
    mutationFn: (id: string) => teacherAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
    },
  });

  const getTeacherById = useCallback(
    async (id: string): Promise<TeachersDetails | null> => {
      try {
        const TeacherDetails = await teacherAPI.getById(id, token);
        return TeacherDetails;
      } catch (err) {
        console.error("Error fetching Teacher by ID:", err);
        return null;
      }
    },
    [token]
  );

  const processCSVTeacherMutation = useMutation<
    CSVTeacherProcessingResult,
    Error,
    CSVTeacher[]
  >({
    mutationFn: async (
      teachers: CSVTeacher[]
    ): Promise<CSVTeacherProcessingResult> => {
      try {
        const result = await teacherAPI.createBatch(teachers, token);

        return {
          success: result.success,
          errors: result.errors.map((err: any) => ({
            id: err.id,
            teacher: {
              name: err.teacher.name,
              email: err.teacher.email,
              isValid: false,
              is_restore: err.is_restore,
            },
            error: err.error || "Unknown error",
          })),
          total: result.total,
        };
      } catch (error: any) {
        // Captura os erros retornados pelo backend
        let errorMessage = "Erro desconhecido";
        let errors: CSVTeacherProcessingError[] = [];

        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          const backendError = error.response.data.error;
          errorMessage = backendError.message || errorMessage;

          // Se o backend retornar erros detalhados
          if (backendError.errors && Array.isArray(backendError.errors)) {
            errors = backendError.errors;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        return {
          success: 0,
          errors:
            errors.length > 0
              ? errors.map((err) => ({
                  id: err.id,
                  teacher: {
                    name: err.teacher?.name || "Nome não disponível",
                    email: err.teacher?.email || "Email não disponível",
                    isValid: false,
                  },
                  error: err.error || errorMessage,
                  is_restore: err.teacher.is_restore,
                }))
              : teachers.map((teacher, index) => ({
                  id: `error-${index}`,
                  teacher: { ...teacher, isValid: false },
                  error: errorMessage,
                  is_restore: false,
                })),
          total: teachers.length,
        };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
    },
  });

  const restoreByBatchTeacherMutation = useMutation({
    mutationFn: (id: string) => teacherAPI.restoreByBatch(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
    },
  });

  const restoreByEmailTeacherMutation = useMutation({
    mutationFn: (email: string) => teacherAPI.restoreByEmail(email, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
    },
  });

  return {
    teachers,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createTeacherAsync: createTeacherMutation.mutateAsync,
    updateTeacher: updateTeacherMutation.mutate,
    deleteTeacher: deleteTeacherMutation.mutate,
    getTeacherById,
    changePage,
    isCreating: createTeacherMutation.isPending,
    isUpdating: updateTeacherMutation.isPending,
    isDeleting: deleteTeacherMutation.isPending,
    processCSVTeacher: processCSVTeacherMutation.mutateAsync,
    isProcessingCSVTeacher: processCSVTeacherMutation.isPending,
    CSVTeacherError: processCSVTeacherMutation.error,
    restoreByBatchTeacher: restoreByBatchTeacherMutation.mutate,
    restoreByEmailTeacher: restoreByEmailTeacherMutation.mutate,
  };
};
